import { Box, Typography } from '@mui/material';
import moment from 'moment';
import React from 'react';
import { COLOR as SHARED_COLOR } from 'shared/constants/colors';
import { SubscriptionPlanResponse } from 'shared/types/Subscription';

import { TickIcon } from '@/assets/svg';
import { useSubscriptionContext } from '@/context/SubscriptionContext';
import { SEATS_LIMIT_FOR_NON_PREMIUM } from '@/hooks/useReachedTeamSeatsLimit';
import { t } from '@/services/translations/config';
import { COLOR } from '@/theme/colors';

import { getFormattedPrice } from '../SubscriptionPlans/utils';

const CurrentSubscriptionPlan: React.FC = () => {
    const {
        currentOrganisationSubscription,
        currentOrganisationSubscriptionPlan,
        isCurrentSubscriptionInternalTrial,
    } = useSubscriptionContext();

    const isSubscriptionCanceled = currentOrganisationSubscription?.canceled_at;

    if (isCurrentSubscriptionInternalTrial) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    mx: 'auto',
                    maxWidth: '1300px',
                    width: '100%',
                    px: '24px',
                }}
            >
                <Typography fontWeight={700} fontSize={16}>
                    {t('PremiumPage:currentSubscriptionPlan:title')}
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        maxWidth: '600px',
                        border: `1px solid ${COLOR.lightGray}`,
                        padding: '18px',
                        borderRadius: '8px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '6px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            <Box
                                sx={{
                                    borderRadius: '50px',
                                    width: '14px',
                                    height: '14px',
                                    padding: '2.5px',
                                    backgroundColor: COLOR.activeBlack,
                                }}
                            >
                                <TickIcon
                                    color={COLOR.white}
                                    width={10}
                                    height={10}
                                />
                            </Box>
                            <Typography
                                fontSize={14}
                                fontWeight={700}
                                color={COLOR.ebonyClay}
                            >
                                EQUINET{' '}
                                {t('PremiumPage:subscriptionPlans:teams:name')}{' '}
                                {`(${
                                    currentOrganisationSubscriptionPlan?.seats ||
                                    SEATS_LIMIT_FOR_NON_PREMIUM
                                } ${t('SubscriptionDetails:seats')})`}
                            </Typography>
                        </Box>
                        <Typography fontSize={12} color={COLOR.regentGray}>
                            {t('SubscriptionDetails:early_access')}
                        </Typography>
                        {currentOrganisationSubscription?.valid_until && (
                            <Typography
                                fontSize={12}
                                color={COLOR.black}
                                fontWeight={700}
                            >
                                {t('SubscriptionDetails:date:active_until', {
                                    date: moment(
                                        currentOrganisationSubscription?.valid_until,
                                    ).format('MMM Do, yyyy'),
                                })}
                            </Typography>
                        )}
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            fontSize={14}
                            fontWeight={700}
                            color={COLOR.ebonyClay}
                            textAlign="right"
                        >
                            {t('SubscriptionDetails:free')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                mx: 'auto',
                maxWidth: '1300px',
                width: '100%',
                px: '24px',
            }}
        >
            <Typography fontWeight={700} fontSize={16}>
                {t('PremiumPage:currentSubscriptionPlan:title')}
            </Typography>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    maxWidth: '600px',
                    border: `1px solid ${COLOR.lightGray}`,
                    padding: '18px',
                    borderRadius: '8px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '4px',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}
                    >
                        <Box
                            sx={{
                                borderRadius: '50px',
                                width: '14px',
                                height: '14px',
                                padding: '2.5px',
                                backgroundColor: COLOR.activeBlack,
                            }}
                        >
                            <TickIcon
                                color={COLOR.white}
                                width={10}
                                height={10}
                            />
                        </Box>
                        <Typography
                            fontSize={14}
                            fontWeight={700}
                            color={COLOR.ebonyClay}
                        >
                            EQUINET{' '}
                            {currentOrganisationSubscriptionPlan &&
                            currentOrganisationSubscriptionPlan.seats > 1
                                ? t('PremiumPage:subscriptionPlans:teams:name')
                                : t(
                                      'PremiumPage:subscriptionPlans:premium:name',
                                  )}
                        </Typography>
                    </Box>
                    <Typography fontSize={12} color={COLOR.regentGray}>
                        {currentOrganisationSubscriptionPlan?.platform ===
                        'android'
                            ? t(
                                  'PremiumPage:currentSubscriptionPlan:androidSubscription',
                              )
                            : t(
                                  'PremiumPage:currentSubscriptionPlan:iosSubscription',
                              )}
                    </Typography>
                    {isSubscriptionCanceled && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                            }}
                        >
                            <Typography fontSize={12} color={COLOR.regentGray}>
                                {t('SubscriptionDetails:canceled')}
                            </Typography>
                            {currentOrganisationSubscription?.valid_until && (
                                <Typography
                                    fontSize={12}
                                    color={SHARED_COLOR.maroon}
                                    fontWeight={700}
                                >
                                    {t('SubscriptionDetails:date:expires_on', {
                                        date: moment(
                                            currentOrganisationSubscription?.valid_until,
                                        ).format('MMM Do, yyyy'),
                                    })}
                                </Typography>
                            )}
                        </Box>
                    )}
                    {!isSubscriptionCanceled && (
                        <Typography fontSize={12} color={COLOR.regentGray}>
                            {currentOrganisationSubscriptionPlan?.billing_period ===
                            'monthly'
                                ? t('SubscriptionDetails:period:monthly')
                                : t('SubscriptionDetails:period:yearly')}
                        </Typography>
                    )}
                    {!isSubscriptionCanceled &&
                        currentOrganisationSubscription?.upcoming_payment
                            ?.date && (
                            <Typography fontSize={12} color={COLOR.regentGray}>
                                {t('SubscriptionDetails:date:next_payment', {
                                    date: moment(
                                        currentOrganisationSubscription
                                            .upcoming_payment.date,
                                    ).format('MMM Do, yyyy'),
                                })}
                            </Typography>
                        )}
                </Box>
                {!isSubscriptionCanceled && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Typography
                            fontSize={14}
                            fontWeight={700}
                            color={COLOR.ebonyClay}
                            textAlign="right"
                        >
                            {getFormattedPrice(
                                currentOrganisationSubscriptionPlan as SubscriptionPlanResponse,
                            )}
                            /
                            {currentOrganisationSubscriptionPlan?.billing_period ===
                            'monthly'
                                ? t('SubscriptionDetails:period:month')
                                : t('SubscriptionDetails:period:year')}
                        </Typography>

                        {currentOrganisationSubscriptionPlan?.platform ===
                            'android' && (
                            <Typography
                                color={COLOR.deepCerulean}
                                fontSize={12}
                                component="button"
                                onClick={() =>
                                    window.open(
                                        'https://play.google.com/store/account/subscriptions',
                                        '_blank',
                                    )
                                }
                            >
                                {t(
                                    'PremiumPage:currentSubscriptionPlan:manageOnGoogle',
                                )}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default CurrentSubscriptionPlan;
